import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { FilterCompleteService } from 'src/app/filter-complete/filter-complete';
import { IFilterComponent, SearchFilter } from 'src/app/infrastructure/iFilter-component';
import { BillingStateFilter } from 'src/app/model/billing/billing-state-filter';
import { CenterFilter } from 'src/app/model/center-filter';
import { Entity } from 'src/app/model/entity';
import { PatientSearch } from 'src/app/model/patient';
import { PhysicianFilter } from 'src/app/model/physician-Filter';
import { ServiceFilter } from 'src/app/model/service-filter';
import { SpecialityFilter } from 'src/app/model/speciality-filter';
import { SearchMedicalReportAuditRequest } from 'src/app/model/study-report/search-medical-report-audit-request';
import { SearchMedicalReportCategory } from 'src/app/model/study-report/search-medical-report-category-response';
import { StudyRegionFilter } from 'src/app/model/study-report/study-region-filter';
import { AuthService } from 'src/app/services/auth.service';
import { FilterRecordService } from 'src/app/services/filter-record.service';
import { FilterService } from 'src/app/services/filter.service';
import { MedicalServiceService } from 'src/app/services/medical-service.service';
import { StudyReportService } from 'src/app/services/study-report/study-report.service';

@Component({
  selector: 'app-study-report-audit-filter',
  templateUrl: './study-report-audit-filter.component.html',
  styleUrls: ['./study-report-audit-filter.component.css']
})
export class StudyReportAuditFilterComponent implements IFilterComponent, OnInit {
  form: FormGroup;
  @ViewChild("formElement", { static: false }) formElement!: ElementRef;
  filterCompleteEntities: FilterCompleteService<Entity>;
  filterCompleteReferringPhysicians: FilterCompleteService<PhysicianFilter>;
  filterCompleteStudyReportPhysicians: FilterCompleteService<PhysicianFilter>;
  filterCompleteCenters: FilterCompleteService<CenterFilter>;
  filterCompleteServices: FilterCompleteService<ServiceFilter>;
  filterCompleteSpecialties: FilterCompleteService<SpecialityFilter>;

  filterCompleteStudyRegions: FilterCompleteService<StudyRegionFilter>;
  billingStates: BillingStateFilter[] = [];
  medicalReportCategories: SearchMedicalReportCategory[] = [];

  seeAllStudies: boolean;
  myStudiesChecked: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private filterService: FilterService,
    private medicalServiceService: MedicalServiceService,
    private authService: AuthService,
    private studyReportService: StudyReportService,
    private filterRecordService: FilterRecordService
  ) {
    const date = new Date();
    this.seeAllStudies = authService.seeAllStudies();
    this.myStudiesChecked = authService.myStudiesChecked();

    this.form = this.formBuilder.group({
      DateFrom: [date],
      DateTo: [date],
      Patient: [null],
      Entity: [null],
      ReferringPhysician: [null],
      StudyReportPhysician: [null],
      StudyRegion: [null],
      Center: [null],
      Service: [null],
      Specialty: [null],
      Category: [null],
      BillingStates: [[]],
      Protocol: [""],
      SeeToInform: [true],
      SeeToCorrect: [false],
      SeeConfirmed: [false],
      WithAdminControled: [false],
      ReAppointment: [false],
      AppointmentDate: [null],
      OrderByPatient: [false],
      MyStudyReport: [false],
      SeeOnlyNoClassified: [false],
      SeePublished: [false],
      SeeNoPublished: [false],
      SeeOnlyRegistered: [false],
    });

    this.filterCompleteEntities = new FilterCompleteService<Entity>(
      [],
      this.form.controls.Entity
    );

    this.filterCompleteServices = new FilterCompleteService<PhysicianFilter>(
      [],
      this.form.controls.Service
    );
    this.filterCompleteSpecialties = new FilterCompleteService<SpecialityFilter>(
      [],
      this.form.controls.Specialty
    );
    this.filterCompleteReferringPhysicians =
      new FilterCompleteService<PhysicianFilter>(
        [],
        this.form.controls.ReferringPhysician
      );
    this.filterCompleteStudyReportPhysicians =
      new FilterCompleteService<PhysicianFilter>(
        [],
        this.form.controls.StudyReportPhysician
      );
    this.filterCompleteCenters = new FilterCompleteService<PhysicianFilter>(
      [],
      this.form.controls.Center
    );
    this.filterCompleteStudyRegions =
      new FilterCompleteService<StudyRegionFilter>(
        [],
        this.form.controls.StudyRegion
      );
  }

  get f() {
    return this.form.controls;
  }

  async saveFilters() {
    this.filterRecordService.saveFilters(
      "StudyReportAuditFilterComponent",
      this.form
    );
  }

  setFilters(): boolean {
    return this.filterRecordService.setFilters(
      "StudyReportAuditFilterComponent",
      this.form
    );
  }

  getSearchFilters(): SearchFilter[] {
    const requiredTags: string[] = [];
    requiredTags.push("DateFrom");
    requiredTags.push("DateTo");
    return this.filterRecordService.convertToSearchFilters(
      this.form,
      requiredTags
    );
  }

  get filteredStudyRegionsOptions() {
    return this.filterCompleteStudyRegions
      ? this.filterCompleteStudyRegions.filteredOptions
      : [];
  }

  get filteredEntitiesOptions() {
    return this.filterCompleteEntities
      ? this.filterCompleteEntities.filteredOptions
      : [];
  }

  get filteredReferringPhysiciansOptions() {
    return this.filterCompleteReferringPhysicians
      ? this.filterCompleteReferringPhysicians.filteredOptions
      : [];
  }
  get filteredStudyReportPhysiciansOptions() {
    return this.filterCompleteStudyReportPhysicians
      ? this.filterCompleteStudyReportPhysicians.filteredOptions
      : [];
  }
  get filteredCentersOptions() {
    return this.filterCompleteCenters
      ? this.filterCompleteCenters.filteredOptions
      : [];
  }
  get filteredServicesOptions() {
    return this.filterCompleteServices
      ? this.filterCompleteServices.filteredOptions
      : [];
  }

  get filteredSpecialtiesOptions() {
    return this.filterCompleteSpecialties
      ? this.filterCompleteSpecialties.filteredOptions
      : [];
  }

  private asArray<T>(value: T[] | null | undefined): T[] {
    return Array.isArray(value) ? value : [];
  }

  private parseMedicalReportId(): number | undefined {
    const protocol = this.form.controls.Protocol.value;
    if (protocol === null || protocol === undefined || protocol === '') {
      return undefined;
    }

    const parsed = Number(protocol);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  async search(): Promise<any> {
    const selectedEntity = this.form.controls.Entity.value as Entity;
    const selectedSpecialty = this.form.controls.Specialty.value as SpecialityFilter;
    const selectedService = this.form.controls.Service.value as ServiceFilter;
    const selectedCategory = this.form.controls.Category
      .value as SearchMedicalReportCategory;
    const selectedBillingStates = this.form.controls.BillingStates.value as BillingStateFilter[];

    const request = new SearchMedicalReportAuditRequest();
    request.MedicalReportId = this.parseMedicalReportId();
    request.EntityId = selectedEntity ? selectedEntity.Id : 0;
    request.FromDate = this.form.controls.DateFrom.value || undefined;
    request.ToDate = this.form.controls.DateTo.value || undefined;
    request.SpecialtyId = selectedSpecialty ? selectedSpecialty.Id : undefined;
    request.ServiceId = selectedService ? selectedService.Id : undefined;
    request.CategoryId = selectedCategory ? selectedCategory.Id : undefined;
    request.OnlyUnclassified = !!this.form.controls.SeeOnlyNoClassified.value;
    request.IncludeExported = !!this.form.controls.SeePublished.value;
    request.IncludeNotExported = !!this.form.controls.SeeNoPublished.value;
    request.BillingStatusIds = selectedBillingStates
      ? selectedBillingStates.map((state) => state.Id)
      : [];
    request.NonBillableReasonIds = [];
    request.OnlyRegistered = !!this.form.controls.SeeOnlyRegistered.value;

    return await this.studyReportService.searchMedicalReportAudit(request);
  }

  ngOnInit(): void {
    if (
      this.myStudiesChecked &&
      !this.filterRecordService.existsFilters("StudyReportAuditFilterComponent")
    ) {
      this.form.controls.MyStudyReport.setValue(true);
    }

    this.filterService.getPhysiciansFilter().then((physiciansFilter) => {
      this.filterCompleteReferringPhysicians =
        new FilterCompleteService<PhysicianFilter>(
          physiciansFilter,
          this.form.controls.ReferringPhysician
        );
      this.filterCompleteStudyReportPhysicians =
        new FilterCompleteService<PhysicianFilter>(
          physiciansFilter,
          this.form.controls.StudyReportPhysician
        );
    });
    this.filterService.getCentersFilter().then((centers) => {
      this.filterCompleteCenters = new FilterCompleteService<CenterFilter>(
        centers,
        this.form.controls.Center
      );
    });
    this.filterService.getEntitiesFilter(true).then((entities) => {
      this.filterCompleteEntities = new FilterCompleteService<Entity>(
        entities,
        this.form.controls.Entity
      );
    });
    this.filterService.getServicesFilter().then((services) => {
      this.filterCompleteServices = new FilterCompleteService<ServiceFilter>(
        services,
        this.form.controls.Service
      );
    });
    this.filterService.getSpecialtiesFilter().then((specialties) => {
      this.filterCompleteSpecialties = new FilterCompleteService<SpecialityFilter>(
        specialties,
        this.form.controls.Specialty
      );
    });
    this.filterService.getBillingStateFilter().then((billingStates) => {
      this.billingStates = this.asArray(billingStates);
    });

    this.studyReportService.getMedicalReportCategories().then((categories) => {
      this.medicalReportCategories = this.asArray(categories);
    });

    this.medicalServiceService.getStudyRegionFilter().then((studyRegions) => {
      this.filterCompleteStudyRegions =
        new FilterCompleteService<StudyRegionFilter>(
          studyRegions,
          this.form.controls.StudyRegion
        );
    });
  }
}
